import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../../context/AppContext';
import { 
  Star, 
  HelpCircle, 
  Settings, 
  BookOpen, 
  ArrowLeft, 
  Calendar, 
  Check, 
  Plus, 
  Trash2, 
  Users, 
  Search, 
  ChevronDown, 
  AlertCircle,
  ArrowRightLeft,
  UserCheck
} from 'lucide-react';

const LeavePolicyManagement = () => {
  const { 
    users, 
    leavePolicyGroups, 
    updateLeavePolicy, 
    createLeavePolicyGroup, 
    deleteLeavePolicyGroup, 
    assignUserToPolicyGroup 
  } = useContext(AppContext);

  const [selectedGroupId, setSelectedGroupId] = useState(leavePolicyGroups[0]?.id || 'policy-default');
  const [activeTab, setActiveTab] = useState('settings'); // 'settings' | 'members'
  
  // Current active group
  const activeGroup = leavePolicyGroups.find(g => g.id === selectedGroupId) || leavePolicyGroups[0];

  // Local Form state initialized from active group settings
  const [formData, setFormData] = useState({ ...activeGroup.settings });
  const [groupName, setGroupName] = useState(activeGroup.name);
  const [isSavedNotice, setIsSavedNotice] = useState(false);

  // Group creation modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');

  // Member management state
  const [memberSearch, setMemberSearch] = useState('');
  const [memberDeptFilter, setMemberDeptFilter] = useState('ALL');
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [targetMoveGroupId, setTargetMoveGroupId] = useState('');
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [addCandidateIds, setAddCandidateIds] = useState([]);

  // Sync formData when selected group changes
  React.useEffect(() => {
    if (activeGroup) {
      setFormData({ ...activeGroup.settings });
      setGroupName(activeGroup.name);
      setSelectedMemberIds([]);
    }
  }, [selectedGroupId, activeGroup]);

  // Handle Save
  const handleSavePolicy = (e) => {
    e?.preventDefault();
    updateLeavePolicy(selectedGroupId, formData, groupName);
    setIsSavedNotice(true);
    setTimeout(() => setIsSavedNotice(false), 3000);
  };

  // Handle Create Group
  const handleCreateGroup = (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) {
      alert('그룹 이름을 입력하세요.');
      return;
    }
    const created = createLeavePolicyGroup(newGroupName.trim(), newGroupDesc.trim());
    setNewGroupName('');
    setNewGroupDesc('');
    setIsCreateModalOpen(false);
    setSelectedGroupId(created.id);
    alert(`'${created.name}' 연차 정책 그룹이 성공적으로 생성되었습니다.`);
  };

  // Handle Delete Group
  const handleDeleteGroup = (groupId) => {
    if (confirm('이 연차 정책 그룹을 삭제하시겠습니까? 소속된 임직원은 기본 그룹으로 자동 이전됩니다.')) {
      deleteLeavePolicyGroup(groupId);
      setSelectedGroupId('policy-default');
    }
  };

  // Handle Member Bulk Move
  const handleMoveSelectedMembers = () => {
    if (selectedMemberIds.length === 0) {
      alert('이동할 임직원을 먼저 선택하세요.');
      return;
    }
    if (!targetMoveGroupId) {
      alert('이동할 대상 정책 그룹을 선택하세요.');
      return;
    }
    selectedMemberIds.forEach(uid => {
      assignUserToPolicyGroup(uid, targetMoveGroupId);
    });
    setSelectedMemberIds([]);
    alert(`선택한 ${selectedMemberIds.length}명의 임직원이 이동되었습니다.`);
  };

  // Handle Add Member into Current Group
  const handleAddMembersToCurrentGroup = () => {
    if (addCandidateIds.length === 0) {
      alert('추가할 직원을 선택하세요.');
      return;
    }
    addCandidateIds.forEach(uid => {
      assignUserToPolicyGroup(uid, selectedGroupId);
    });
    setAddCandidateIds([]);
    setIsAddMemberModalOpen(false);
    alert(`${addCandidateIds.length}명의 직원이 '${activeGroup.name}' 그룹에 배정되었습니다.`);
  };

  // Filtered members in current group
  const groupMembers = users.filter(u => activeGroup.memberUserIds?.includes(u.UserID));
  const filteredGroupMembers = groupMembers.filter(m => {
    const matchText = (m.Name + m.Account + m.Department + m.JobTitle).toLowerCase().includes(memberSearch.toLowerCase());
    const matchDept = memberDeptFilter === 'ALL' || m.Department === memberDeptFilter;
    return matchText && matchDept;
  });

  // Non-group members candidate for adding
  const nonGroupMembers = users.filter(u => !activeGroup.memberUserIds?.includes(u.UserID));

  return (
    <div className="space-y-6 text-left font-sans">
      {/* 1. Header with Icons matching screenshot */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-3 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-black tracking-tight text-slate-900">
            연차정책 관리
          </h1>
          <div className="flex items-center gap-2 text-slate-400 pl-1">
            <button title="즐겨찾기" className="p-1 hover:text-amber-500 transition-colors cursor-pointer">
              <Star className="w-5 h-5" />
            </button>
            <button title="도움말" className="p-1 hover:text-indigo-600 transition-colors cursor-pointer">
              <HelpCircle className="w-5 h-5" />
            </button>
            <button title="설정" className="p-1 hover:text-slate-700 transition-colors cursor-pointer">
              <Settings className="w-5 h-5" />
            </button>
            <button title="규정집" className="p-1 hover:text-indigo-600 transition-colors cursor-pointer">
              <BookOpen className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Group Selector Pills */}
        <div className="flex flex-wrap items-center gap-2">
          {leavePolicyGroups.map(g => (
            <button
              key={g.id}
              onClick={() => setSelectedGroupId(g.id)}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                selectedGroupId === g.id
                  ? 'bg-slate-900 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <span>{g.name}</span>
              <span className={`text-[10px] px-1.5 py-0.2 rounded-md ${
                selectedGroupId === g.id ? 'bg-slate-700 text-slate-200' : 'bg-slate-200 text-slate-500'
              }`}>
                {g.memberUserIds?.length || 0}
              </span>
            </button>
          ))}
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="px-3 py-1.5 rounded-xl text-xs font-bold bg-cyan-50 border border-cyan-200 text-cyan-700 hover:bg-cyan-100 transition-all cursor-pointer flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>그룹 추가</span>
          </button>
        </div>
      </div>

      {/* 2. Main Card Container */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-8 shadow-xs space-y-8">
        {/* Top Group Name Banner & Action Tabs */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xl font-bold text-slate-900">
              <ArrowLeft className="w-5 h-5 text-slate-600 cursor-pointer hover:text-slate-900" onClick={() => setSelectedGroupId('policy-default')} />
              <span>{activeGroup.name}</span>
              {activeGroup.isDefault && (
                <span className="text-xs px-2 py-0.5 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-700 font-bold">
                  기본 정책
                </span>
              )}
            </div>

            {!activeGroup.isDefault && (
              <button
                onClick={() => handleDeleteGroup(activeGroup.id)}
                className="text-xs font-bold text-rose-600 hover:text-rose-700 bg-rose-50 border border-rose-200 px-3 py-1.5 rounded-xl transition-all cursor-pointer flex items-center gap-1"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>그룹 삭제</span>
              </button>
            )}
          </div>

          {/* Sub Tabs: [연차 정책 설정] | [적용 멤버 관리 N] */}
          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-5 py-2.5 rounded-full text-xs font-bold transition-all cursor-pointer ${
                activeTab === 'settings'
                  ? 'bg-slate-900 text-white shadow-xs'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              연차 정책 설정
            </button>

            <button
              onClick={() => setActiveTab('members')}
              className={`px-5 py-2.5 rounded-full text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                activeTab === 'members'
                  ? 'bg-slate-900 text-white shadow-xs'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              <span>적용 멤버 관리</span>
              <span className={`text-[11px] font-bold px-2 py-0.2 rounded-full ${
                activeTab === 'members' ? 'bg-cyan-500 text-white' : 'bg-cyan-50 text-cyan-700'
              }`}>
                {activeGroup.memberUserIds?.length || 0}
              </span>
            </button>
          </div>
        </div>

        {/* Tab 1: [연차 정책 설정] Form matching the screenshot */}
        {activeTab === 'settings' && (
          <form onSubmit={handleSavePolicy} className="space-y-7 max-w-3xl">
            {/* 1. 연차 생성 기준 */}
            <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
              <label className="text-xs md:text-sm font-bold text-slate-700 flex items-center gap-1">
                <span>연차 생성 기준</span>
                <span className="text-rose-500 font-black">*</span>
              </label>
              <div className="md:col-span-3 flex flex-wrap items-center gap-3">
                <select
                  value={formData.standard}
                  onChange={(e) => setFormData(prev => ({ ...prev, standard: e.target.value }))}
                  className="px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-xs md:text-sm font-bold text-slate-800 focus:outline-none focus:border-cyan-600 transition-all cursor-pointer"
                >
                  <option value="FISCAL_YEAR">회계연도</option>
                  <option value="JOIN_DATE">입사일 기준</option>
                  <option value="NONE">연차 미발생</option>
                </select>

                {formData.standard === 'FISCAL_YEAR' && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-500">회계연도 기준일</span>
                    <div className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs md:text-sm text-slate-800">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={formData.fiscalDate || '01-01'}
                        onChange={(e) => setFormData(prev => ({ ...prev, fiscalDate: e.target.value }))}
                        className="w-16 focus:outline-none font-bold"
                        placeholder="MM-DD"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 2. 연차/월차 자동발생 */}
            <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
              <label className="text-xs md:text-sm font-bold text-slate-700 flex items-center gap-1">
                <span>연차/월차 자동발생</span>
                <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-pointer" title="설정한 기준일에 연차가 시스템에서 자동 부여됩니다." />
              </label>
              <div className="md:col-span-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, autoGrant: !prev.autoGrant }))}
                  className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors cursor-pointer ${
                    formData.autoGrant ? 'bg-cyan-600' : 'bg-slate-300'
                  }`}
                >
                  <div className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform ${
                    formData.autoGrant ? 'translate-x-6' : 'translate-x-0'
                  }`} />
                </button>
              </div>
            </div>

            {/* 3. 연차 사용 단위 & 부분휴가 사용 허용 */}
            <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
              <label className="text-xs md:text-sm font-bold text-slate-700 flex items-center gap-1">
                <span>연차 사용 단위</span>
                <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-pointer" title="직원이 연차 신청 시 사용할 수 있는 최소 단위를 지정합니다." />
              </label>
              <div className="md:col-span-3 flex flex-wrap items-center gap-6">
                <select
                  value={formData.unit}
                  onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
                  className="px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-xs md:text-sm font-bold text-slate-800 focus:outline-none focus:border-cyan-600 transition-all cursor-pointer"
                >
                  <option value="0.25">반반차(0.25)</option>
                  <option value="0.5">반차(0.5)</option>
                  <option value="1.0">종일(1.0)</option>
                </select>

                <div className="flex items-center gap-3">
                  <span className="text-xs md:text-sm font-bold text-slate-700">부분휴가 사용 허용</span>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, allowPartial: !prev.allowPartial }))}
                    className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors cursor-pointer ${
                      formData.allowPartial ? 'bg-cyan-600' : 'bg-slate-300'
                    }`}
                  >
                    <div className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform ${
                      formData.allowPartial ? 'translate-x-6' : 'translate-x-0'
                    }`} />
                  </button>
                </div>
              </div>
            </div>

            {/* 4. 연차 초과 사용 허용 (연차 당겨쓰기) */}
            <div className="grid grid-cols-1 md:grid-cols-4 items-start gap-4">
              <label className="text-xs md:text-sm font-bold text-slate-700 flex items-center gap-1 pt-1">
                <span>연차 초과 사용 허용</span>
                <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-pointer" title="잔여 연차가 부족할 때 다음 달 또는 다음 연도 발생분을 당겨 쓸 수 있는지 설정합니다." />
              </label>
              <div className="md:col-span-3 space-y-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, allowOveruse: !prev.allowOveruse }))}
                  className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors cursor-pointer ${
                    formData.allowOveruse ? 'bg-cyan-600' : 'bg-slate-300'
                  }`}
                >
                  <div className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform ${
                    formData.allowOveruse ? 'translate-x-6' : 'translate-x-0'
                  }`} />
                </button>

                {formData.allowOveruse && (
                  <div className="p-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50/70 space-y-3">
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                      허용 범위 설정
                    </span>
                    <div className="space-y-2 text-xs md:text-sm">
                      <label className="flex items-center gap-2 cursor-pointer text-slate-800 font-bold">
                        <input
                          type="radio"
                          name="overuseScope"
                          value="FUTURE_PLANNED"
                          checked={formData.overuseScope === 'FUTURE_PLANNED'}
                          onChange={() => setFormData(prev => ({ ...prev, overuseScope: 'FUTURE_PLANNED' }))}
                          className="text-cyan-600 focus:ring-cyan-500 cursor-pointer"
                        />
                        <span>미래 발생 예정 연차 내에서 허용</span>
                        <HelpCircle className="w-3 h-3 text-slate-400" />
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer text-slate-600">
                        <input
                          type="radio"
                          name="overuseScope"
                          value="UNLIMITED"
                          checked={formData.overuseScope === 'UNLIMITED'}
                          onChange={() => setFormData(prev => ({ ...prev, overuseScope: 'UNLIMITED' }))}
                          className="text-cyan-600 focus:ring-cyan-500 cursor-pointer"
                        />
                        <span>연차 한도 제한 없이 마이너스 사용 허용</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 5. 근태 기준(출근율) 연차 산정 */}
            <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
              <label className="text-xs md:text-sm font-bold text-slate-700 flex items-center gap-1">
                <span>근태 기준(출근율) 연차 산정</span>
                <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-pointer" title="출근율 80% 이상 조건 충족 여부에 따라 연차를 차등 발생시킵니다." />
              </label>
              <div className="md:col-span-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, attendanceBased: !prev.attendanceBased }))}
                  className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors cursor-pointer ${
                    formData.attendanceBased ? 'bg-cyan-600' : 'bg-slate-300'
                  }`}
                >
                  <div className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform ${
                    formData.attendanceBased ? 'translate-x-6' : 'translate-x-0'
                  }`} />
                </button>
              </div>
            </div>

            {/* Form Action Buttons matching screenshot */}
            <div className="flex items-center justify-end gap-3 pt-6 border-t border-slate-100">
              {isSavedNotice && (
                <span className="text-xs font-bold text-cyan-700 bg-cyan-50 px-3 py-1.5 rounded-lg border border-cyan-200 flex items-center gap-1 animate-pulse">
                  <Check className="w-3.5 h-3.5" />
                  정책이 성공적으로 저장되었습니다!
                </span>
              )}
              <button
                type="button"
                onClick={() => setFormData({ ...activeGroup.settings })}
                className="px-6 py-2.5 rounded-xl text-xs md:text-sm font-bold text-slate-600 hover:bg-slate-100 transition-all cursor-pointer"
              >
                취소
              </button>
              <button
                type="submit"
                className="px-7 py-2.5 rounded-xl text-xs md:text-sm font-bold text-white bg-[#00a6b4] hover:bg-[#00929f] shadow-md transition-all cursor-pointer"
              >
                저장
              </button>
            </div>
          </form>
        )}

        {/* Tab 2: [적용 멤버 관리] */}
        {activeTab === 'members' && (
          <div className="space-y-5">
            {/* Action Bar */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <div className="flex flex-wrap items-center gap-2 flex-1 max-w-md">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="이름, 계정, 부서 검색..."
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 focus:bg-white focus:outline-none focus:border-cyan-600"
                  />
                </div>

                <select
                  value={memberDeptFilter}
                  onChange={(e) => setMemberDeptFilter(e.target.value)}
                  className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 focus:bg-white focus:outline-none cursor-pointer"
                >
                  <option value="ALL">전체 부서</option>
                  <option value="클라우드네이티브">클라우드네이티브</option>
                  <option value="AI서비스개발">AI서비스개발</option>
                  <option value="경영마케팅">경영마케팅</option>
                  <option value="경영지원팀">경영지원팀</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                {/* Move Group Button */}
                <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 p-1 rounded-xl">
                  <select
                    value={targetMoveGroupId}
                    onChange={(e) => setTargetMoveGroupId(e.target.value)}
                    className="px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-700 cursor-pointer"
                  >
                    <option value="">이동할 그룹 선택</option>
                    {leavePolicyGroups.filter(g => g.id !== activeGroup.id).map(g => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleMoveSelectedMembers}
                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1"
                  >
                    <ArrowRightLeft className="w-3.5 h-3.5" />
                    <span>그룹 이동</span>
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => setIsAddMemberModalOpen(true)}
                  className="px-4 py-2 bg-[#00a6b4] hover:bg-[#00929f] text-white rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shadow-xs"
                >
                  <UserCheck className="w-4 h-4" />
                  <span>멤버 배정 추가</span>
                </button>
              </div>
            </div>

            {/* Members Table */}
            <div className="overflow-x-auto border border-slate-200 rounded-2xl">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-50 text-slate-500 font-bold border-b border-slate-200">
                  <tr>
                    <th className="p-3 w-10 text-center">
                      <input
                        type="checkbox"
                        checked={filteredGroupMembers.length > 0 && selectedMemberIds.length === filteredGroupMembers.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedMemberIds(filteredGroupMembers.map(m => m.UserID));
                          } else {
                            setSelectedMemberIds([]);
                          }
                        }}
                        className="rounded border-slate-300 text-cyan-600 focus:ring-cyan-500 cursor-pointer"
                      />
                    </th>
                    <th className="p-3">사원명</th>
                    <th className="p-3">계정(ID)</th>
                    <th className="p-3">직위 / 직책</th>
                    <th className="p-3">소속 부서</th>
                    <th className="p-3">입사일</th>
                    <th className="p-3">재직 상태</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredGroupMembers.map(member => (
                    <tr key={member.UserID} className="hover:bg-slate-50/80 transition-colors">
                      <td className="p-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedMemberIds.includes(member.UserID)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedMemberIds(prev => [...prev, member.UserID]);
                            } else {
                              setSelectedMemberIds(prev => prev.filter(id => id !== member.UserID));
                            }
                          }}
                          className="rounded border-slate-300 text-cyan-600 focus:ring-cyan-500 cursor-pointer"
                        />
                      </td>
                      <td className="p-3 font-bold text-slate-900 flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-indigo-50 border border-indigo-100 text-indigo-700 font-black flex items-center justify-center text-xs">
                          {member.Name.charAt(0)}
                        </div>
                        <span>{member.Name}</span>
                      </td>
                      <td className="p-3 font-mono text-slate-600">{member.Account}</td>
                      <td className="p-3 text-slate-700">
                        {member.Position || member.JobTitle || '-'} {member.Duty && `(${member.Duty})`}
                      </td>
                      <td className="p-3">
                        <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-medium">
                          {member.Department || '미배정'}
                        </span>
                      </td>
                      <td className="p-3 text-slate-500 font-mono">{member.JoinDate || '-'}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          member.Status === '정상' || member.EmploymentStatus === '재직'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'bg-rose-50 text-rose-700 border border-rose-200'
                        }`}>
                          {member.EmploymentStatus || member.Status || '재직'}
                        </span>
                      </td>
                    </tr>
                  ))}

                  {filteredGroupMembers.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-slate-400 font-medium">
                        이 정책 그룹에 배정된 직원이 없습니다. 상단의 '멤버 배정 추가' 버튼을 눌러 직원을 배정하세요.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modal 1: Create Policy Group */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-4 text-left">
            <h3 className="text-lg font-bold text-slate-900">새 연차 정책 그룹 추가</h3>
            <p className="text-xs text-slate-500">
              연차 미발생, 수습직원, 파트타임 등 특별 규정이 적용될 정책 그룹을 생성합니다.
            </p>

            <form onSubmit={handleCreateGroup} className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">그룹 이름 *</label>
                <input
                  type="text"
                  placeholder="예: 휴가제한 그룹, 계약직 그룹"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs md:text-sm font-medium focus:bg-white focus:outline-none focus:border-cyan-600"
                  required
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">설명</label>
                <input
                  type="text"
                  placeholder="정책 그룹에 대한 간단한 설명을 입력하세요"
                  value={newGroupDesc}
                  onChange={(e) => setNewGroupDesc(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs md:text-sm font-medium focus:bg-white focus:outline-none focus:border-cyan-600"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 cursor-pointer"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-[#00a6b4] hover:bg-[#00929f] shadow-md cursor-pointer"
                >
                  그룹 생성
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal 2: Add Candidate Members to Current Group */}
      {isAddMemberModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 max-w-lg w-full shadow-2xl space-y-4 text-left">
            <h3 className="text-lg font-bold text-slate-900">
              '{activeGroup.name}' 그룹에 멤버 배정
            </h3>
            <p className="text-xs text-slate-500">
              다른 그룹에 속한 임직원을 선택하여 이 정책 그룹으로 이전 배정합니다.
            </p>

            <div className="max-h-60 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-xl">
              {nonGroupMembers.map(cand => (
                <label key={cand.UserID} className="p-3 flex items-center justify-between hover:bg-slate-50 cursor-pointer">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={addCandidateIds.includes(cand.UserID)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAddCandidateIds(prev => [...prev, cand.UserID]);
                        } else {
                          setAddCandidateIds(prev => prev.filter(id => id !== cand.UserID));
                        }
                      }}
                      className="rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    <div>
                      <strong className="text-xs font-bold text-slate-900">{cand.Name}</strong>
                      <span className="text-[11px] text-slate-500 font-mono block">{cand.Account} / {cand.Department}</span>
                    </div>
                  </div>
                  <span className="text-[10px] text-slate-400 font-bold">
                    {cand.Position || cand.JobTitle}
                  </span>
                </label>
              ))}

              {nonGroupMembers.length === 0 && (
                <div className="p-6 text-center text-xs text-slate-400">
                  모든 임직원이 이미 이 그룹에 배정되어 있습니다.
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  setAddCandidateIds([]);
                  setIsAddMemberModalOpen(false);
                }}
                className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 cursor-pointer"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleAddMembersToCurrentGroup}
                className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-[#00a6b4] hover:bg-[#00929f] shadow-md cursor-pointer"
              >
                선택 멤버 배정 ({addCandidateIds.length}명)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeavePolicyManagement;
